'use client';
import React, { forwardRef, useState, useRef, useEffect, useImperativeHandle, useLayoutEffect } from 'react';
import { MessageContainerPrefixId } from '../../constants/index.js';
import { useGetState } from '../hooks/use-get-state.js';
import { usePrevious } from '../hooks/use-previous.js';
import { FaSolidCheckCircle, FaSolidTimesCircle, FaSolidInfoCircle, FaSolidExclamationCircle, LoadingIcon } from '../icons/index.js';
import styles from './index.module.css.js';

const Icon = {
    success: React.createElement(FaSolidCheckCircle, null),
    error: React.createElement(FaSolidTimesCircle, null),
    info: React.createElement(FaSolidInfoCircle, null),
    warn: React.createElement(FaSolidExclamationCircle, null),
    loading: React.createElement(LoadingIcon, { className: styles['rotate'] }),
};
const Message = forwardRef((props, ref) => {
    const { type, message: originalMessage, duration, getInstance } = props;
    const [currentMessage, setCurrentMessage] = useState(originalMessage);
    const [currentIconType, setCurrentIconType] = useState(type);
    const [nextMessage, setNextMessage] = useState(null);
    const [nextIconType, setNextIconType] = useState();
    const [currentWrapWidth, setCurrentWrapWidth] = useState(undefined);
    const [currentWrapHeight, setCurrentWrapHeight] = useState(undefined);
    // const previousWidth = usePrevious(currentWrapWidth)
    const wrapRef = useRef(null);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!wrapRef.current || duration === Infinity) {
                return;
            }
            wrapRef.current.classList.add(styles['disappear']);
        }, duration || 2500);
        return () => {
            clearTimeout(timer);
        };
    }, []);
    const getCurrentMessage = useGetState(currentMessage);
    const getCurrentIconType = useGetState(currentIconType);
    const getNextMessage = useGetState(nextMessage);
    const getNextIconType = useGetState(nextIconType);
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    const instanceMethodsRef = useRef({
        next(message, type) {
            if (message === getCurrentMessage() && type === getCurrentIconType())
                return;
            setNextMessage(message);
            setNextIconType(type);
        },
        isMount: () => isMounted.current,
    });
    useEffect(() => {
        if (!getInstance)
            return;
        getInstance(instanceMethodsRef.current);
    }, [getInstance]);
    useImperativeHandle(ref, () => instanceMethodsRef.current);
    const fakeHolderRef = useRef(null);
    useLayoutEffect(() => {
        if (getCurrentMessage() === nextMessage) {
            return;
        }
        const $ = fakeHolderRef.current;
        requestAnimationFrame(() => {
            const rect = $.getBoundingClientRect();
            setCurrentWrapWidth(rect.width);
            setCurrentWrapHeight(rect.height);
        });
    }, [nextMessage]);
    const [isTextTransition, setIsTextTransition] = useState(false);
    const messageRef = useRef(null);
    const previousWidth = usePrevious(currentWrapWidth);
    useEffect(() => {
        // if width is not changed, replace immediately
        if (currentWrapWidth === previousWidth) {
            const nextMessage = getNextMessage();
            const nextIconType = getNextIconType();
            nextIconType && setCurrentIconType(nextIconType);
            nextMessage && setCurrentMessage(nextMessage);
        }
    }, [nextMessage]);
    useEffect(() => {
        if (!wrapRef.current)
            return;
        const $wrap = wrapRef.current;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const $message = messageRef.current;
        $wrap.ontransitionstart = () => {
            setIsTextTransition(true);
        };
        $wrap.ontransitionend = () => {
            setIsTextTransition(false);
            const nextMessage = getNextMessage();
            const nextIconType = getNextIconType();
            if (nextMessage) {
                setCurrentMessage(nextMessage);
                setNextMessage(null);
            }
            if (nextIconType) {
                setCurrentIconType(nextIconType);
                setNextIconType(undefined);
            }
        };
        $message.ontransitionstart = (e) => e.stopPropagation();
        $message.ontransitionend = (e) => e.stopPropagation();
        return () => {
            $wrap.ontransitionstart = null;
            $wrap.ontransitionend = null;
            $message.ontransitionstart = null;
            $message.ontransitionend = null;
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: styles['wrap'] },
            React.createElement("div", { className: styles['inner-wrap'], style: {
                    width: currentWrapWidth,
                    height: currentWrapHeight,
                }, ref: wrapRef },
                React.createElement("div", { className: `${styles['icon']} ${isTextTransition ? styles['op-transition'] : ''}` }, Icon[currentIconType]),
                React.createElement("div", { className: `${styles['message']} ${isTextTransition ? styles['text-transition'] : ''}`, ref: messageRef },
                    React.createElement("span", null, currentMessage)))),
        React.createElement("div", { className: `${styles['wrap']} ${styles['hidden']}` },
            React.createElement("div", { className: styles['inner-wrap'], ref: fakeHolderRef },
                React.createElement("div", { className: styles['icon'] }, Icon[type]),
                React.createElement("div", { className: styles['message'] },
                    React.createElement("span", null, nextMessage || currentMessage))))));
});
const MessageContainer = () => {
    return React.createElement("div", { className: styles['container'], id: MessageContainerPrefixId });
};

export { Message, MessageContainer };
